import { type FC } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

interface AirApprovalIndicatorProps {
  /** A — Report PDF approved */
  reportPdfApproved: boolean;
  /** I — Interpretation approved */
  interpretationApproved: boolean;
  /** R — Recommendation approved */
  recommendationApproved: boolean;
  /** Size variant */
  size?: 'small' | 'medium';
}

const INACTIVE_COLOR = '#BDBDBD';

interface LetterConfig {
  letter: string;
  label: string;
  approved: boolean;
}

/**
 * Compact combined indicator showing per-section review approval for a workflow:
 * A = Report PDF, I = Interpretation, R = Recommendation. Approved sections show green,
 * not-yet-approved sections are greyed out. Mirrors {@link SampleTypeIndicator}.
 *
 * Renders as: A I R
 */
export const AirApprovalIndicator: FC<AirApprovalIndicatorProps> = ({
  reportPdfApproved,
  interpretationApproved,
  recommendationApproved,
  size = 'small',
}) => {
  const letters: LetterConfig[] = [
    { letter: 'A', label: 'Report PDF', approved: reportPdfApproved },
    { letter: 'I', label: 'Interpretation', approved: interpretationApproved },
    { letter: 'R', label: 'Recommendation', approved: recommendationApproved },
  ];

  const tooltipText = letters
    .map((l) => `${l.label}: ${l.approved ? 'approved' : 'not approved'}`)
    .join(' · ');

  const isSmall = size === 'small';
  const fontSize = isSmall ? '0.65rem' : '0.75rem';
  const boxHeight = isSmall ? 20 : 24;
  const letterWidth = isSmall ? 16 : 20;

  return (
    <Tooltip title={tooltipText} arrow>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          height: boxHeight,
          borderRadius: '10px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          cursor: 'default',
        }}
      >
        {letters.map((item, index) => (
          <Box
            key={item.label}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: letterWidth,
              height: '100%',
              bgcolor: item.approved ? 'success.main' : 'transparent',
              borderRight: index < letters.length - 1 ? '1px solid' : 'none',
              borderRightColor: 'divider',
            }}
          >
            <Typography
              sx={{
                fontSize,
                fontWeight: 700,
                lineHeight: 1,
                color: item.approved ? '#fff' : INACTIVE_COLOR,
                userSelect: 'none',
              }}
            >
              {item.letter}
            </Typography>
          </Box>
        ))}
      </Box>
    </Tooltip>
  );
};

export default AirApprovalIndicator;
